import { ImageField, Item, RichTextField, TextField } from '@sitecore-jss/sitecore-jss-nextjs';
import type { ComponentProps } from 'lib/component-props';

export type BlogDetailsFields = {
  blogHeading: TextField;
  blogSubHeading: TextField;
  publishedOn: TextField;
  blogPhoto: ImageField;
  blogPost: RichTextField;
  blogDisclaimerText: RichTextField;
  blogShortDescription: TextField;
  blogTopics: BlogTopic[];
  blogAuthor: BlogAuthor | null;
  blogAudience: BlogFilter[];
  numberOfRelatedPosts: TextField;
};

export type BlogAuthorFields = {
  authorName: TextField;
};

export type BlogTopicFields = {
  isClickable: TextField;
  tooltip: TextField;
  topic: TextField;
  color: TextField;
};

export type BlogAuthor = {
  url: string;
  name: string;
  id: string;
  fields: BlogAuthorFields;
};

export type BlogTopic = {
  url: string;
  name: string;
  id: string;
  fields: BlogTopicFields;
};

export type BlogDetailsProps = ComponentProps & {
  fields: BlogDetailsFields;
};

export type BlogListingFiltersProps = {
  posts: BlogPostData[];
  filtersToShowOnLoad: number;
  onFilterChangeCallback: (topics: BlogTopicData[]) => void;
};

export type BlogPostsGridProps = {
  cardColumns: number;
  blogPostsRequest: BlogPostsRequestByFilter;
  description?: RichTextField;
  title?: TextField;
  titleHeadingLevelValue?: string;
  titleTextStyleValue?: string;
  className: string;
  excludeContextItem?: boolean;
};

export type BlogTopicsProps = {
  className?: string;
  language?: string | undefined;
  blogTopics?: BlogTopic[];
  blogTopicsData?: BlogTopicData[];
  allowRemove?: boolean;
};

export type BlogProps = {
  className?: string;
  language?: string | undefined;
  blogDetails: BlogDetailsFields;
};

export type BlogFilter = {
  id: string;
  name?: string;
};

export type BlogAuthorData = BlogFilter & {
  authorName?: string;
};

export type BlogTopicData = BlogFilter & {
  topic?: string;
  tooltip?: string;
  color?: string;
  isClickable?: string;
};

export type FeaturedBlogPostsProps = ComponentProps & {
  fields: {
    title: TextField;
    headingLevel: Item | null;
    textStyle: Item | null;
    description: RichTextField;
    blogAudience: BlogFilter[];
    postsToShow: TextField;
    showFeaturedPostsOnly: TextField;
  };
};

export type BlogListingProps = ComponentProps & {
  fields: {
    title: TextField;
    headingLevel: Item | null;
    textStyle: Item | null;
    description: RichTextField;
    blogAudience: BlogFilter[];
    blogsPerPage: TextField;
    hideFilters: TextField;
    filtersToShowOnLoad: TextField;
  };
};

export interface BlogPostsRequestByFilter {
  language?: string;
  rootItemId?: string;
  searchTerm?: string;
  blogAudience?: string;
  blogTopics?: string;
  database?: string;
  postsToShow?: number;
  showFeaturedPostsOnly?: boolean;
  excludeItemId?: string;
}

export interface BlogPosts {
  posts: BlogPostData[];
}

export interface BlogPostData {
  id: string;
  url: string;
  name: string;
  blogPhoto: string;
  blogHeading: string;
  blogShortDescription: string;
  blogPost: string;
  publishedOn: string;
  isFeatured: string;
  blogTopics: BlogTopicData[];
  blogAuthor: BlogAuthorData[];
}

export enum BlogConstants {
  BlogPostsRoot = '{A6904518-754A-4340-9349-9B773D05AF1A}',
  DefaultFeaturedItems = 3,
  DefaultRelatedColumns = 2,
  BlogSummaryChars = 350,
  DefaultFeaturedHeadingLevel = 'h2',
  DefaultFeaturedHeadingStyle = 'font-style-xl',
  ListingPageSize = 10,
}
